<template>
  <section>
    <div class="filters">
      <div class="fancy_select" v-for="filter in availableFilters" :key="filter.id">
        <select :name="filter.name" @change="updateFilters($event)">
          <option :label="filter.label">{{ filter.label }}</option>
          <option v-for="option in filter.options" :key="option.label" :value="option.label" :selected="activeFilters.get(filter.label) === option.label">
            {{ option.label }}
          </option>
        </select>
      </div>
    </div>
    <h2 class="gallery-header"> Swatches Currently Available </h2>
    <loading-spinner v-if="isLoading" />
    <p v-if="assets.length === 0 && !isLoading"> No samples found. </p>
    <div class="grid" v-if="!isLoading">
      <figure v-for="asset in assets" :key="asset.id" class="gallery-items">
        <img
          :alt="asset.title"
          :asset_id="asset.id"
          :src="asset.image_src || asset.image"
          :sizes="sizes"
          :srcset="asset.srcset"
          @click="handleSampleClick"
        />
        <div class="checkbox_container">
          <input
            type="checkbox"
            class="transparent_checkbox"
            :asset_id="asset.id"
            @click="handleSampleClick"
            :ref="`gallery_checkbox_${asset.id}`"
            :id="`sample_checkbox_${asset.id}`"
          />
          <label
            :for="`sample_checkbox_${asset.id}`"
            :asset_id="asset.id"
            @click="handleSampleClick"
          />
        </div>
        <h2 class="sku">{{ asset.sku.length > 10 ? asset.sku.substring(0, 10) + '...' : asset.sku }}</h2>
      </figure>
    </div>
    <div class="more">
      <button
        class="request_samples"
        @click="openModal()"
        @keyup.enter="openModal()"
        :disabled="assetIds.length === 0"
      >
        Select
      </button>
      <!-- <load-more
        :is-loading="isLoading"
        :pagination="pagination"
        @loadMore="getAssets(true)"
      /> -->
    </div>
    <div class="notification" v-if="notificationTriggered" :class="{visible: notificationVisible}" @click="dismissNotification()">
      <p>{{ notificationText }}</p>
      <button type="button" @click="dismissNotification()"></button>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import EventBus from '../event_bus';
import LoadMore from '../atoms/load_more.js';
import LoadingSpinner from '../atoms/loading_spinner.js';
import ProductFilter from '../atoms/product_filter.js';
import { SAMPLE_GALLERY_LIMIT, MATERIAL_SAMPLE_MODAL_ENDPOINT } from '../constants.js';

export default {
  components: {
    LoadMore,
    ProductFilter,
    LoadingSpinner,
  },
  props: [
    'preload',
    'filters',
    'sizes',
    'widths',
    'volume',
    'close',
  ],
  data: function() {
    return {
      activeFilters: new Map([ ['Color', ''], ['Type', ''], ['Pattern', ''] ]),
      settings: {},
      assets: [],
      assetIds: [],
      pagination: {},
      isLoading: true,
      selectedSamples: new Map(), // { asset_id<string> : checked<bool> }
      notificationText: null,
      notificationTriggered: false,
      notificationVisible: false,
      url: "",
    }
  },
  computed: {
    availableFilters: function() {
      return JSON.parse(this.filters);
    },
  },
  mounted() {
    const params = new URLSearchParams(location.search);
    const viewCollectionStr = params.get("viewCollection");

    if (viewCollectionStr) {
      this.urlParamCollectionIds = viewCollectionStr.split(',');
      this.openModal(true);
    } else {
      this.getAssets();
    }

    EventBus.$on('sampleClicked', (event, modalRefs) => {
      this.handleSampleClick(event, modalRefs);
    })
    EventBus.$on('successfulSampleSubmission', () => this.getAssets());
  },
  methods: {
    handleSampleClick: function ({ target }, modalRefs) {
      if (target.tagName === "IMG" || target.tagName === "LABEL" || target.tagName === "INPUT") {
        const asset_id = target.getAttribute('asset_id');

        if (modalRefs) {
          const modal_checkbox = modalRefs[`modal_checkbox_${asset_id}`][0];
          modal_checkbox.checked = !modal_checkbox.checked;
        }
        if (this.$refs[`gallery_checkbox_${asset_id}`]) {
          const gallery_checkbox = this.$refs[`gallery_checkbox_${asset_id}`][0];
          gallery_checkbox.checked = !gallery_checkbox.checked;
        }

        //set or flip checked bool
        if (this.selectedSamples.has(asset_id)) {
          const checked = this.selectedSamples.get(asset_id);
          this.selectedSamples.set(asset_id, !checked);
        } else {
          this.selectedSamples.set(asset_id, true);
        }
      }
      const selectedIds = [];
      this.selectedSamples.forEach((checked, sampleId) => {
        if (checked) {
          selectedIds.push(parseInt(sampleId))
        }
      });
      this.assetIds = selectedIds;
    },
    updateFilters: function ({ target }) {
      const filterCategory = target[0].value;

      // if filter category (Type, Color etc.) is chosen, that active filter is cleared
      if (this.activeFilters.has(target.value)) {
        this.activeFilters.set(filterCategory, '');
      } else {
        this.activeFilters.set(filterCategory, target.value);
      }
      this.getAssets();
    },
    getAssets: async function(loadMore=false) {
      this.isLoading = true;
      let endpoint = MATERIAL_SAMPLE_MODAL_ENDPOINT;

      if (loadMore && this.pagination.hasOwnProperty("links") && this.pagination.links.hasOwnProperty("next")) {
        endpoint = this.pagination.links.next;
      }

      const params = {
        volume: this.volume,
        limit: SAMPLE_GALLERY_LIMIT,
      };

      if (this.widths) {
        params['widths'] = this.widths;
      }

      try {
        const {
          data: {
            data, meta
          }
        } = await axios.get(endpoint, { params });

        const filteredAssets = data.filter((asset) => this.assetTestCriteria(asset));
        loadMore ? this.assets.push(...filteredAssets) : this.assets = filteredAssets;

        // this.pagination = meta.pagination
        this.isLoading = false;

        if (meta.pagination.current_page >= meta.pagination.total_pages) {
          hideLoadButton = true;
        }
      } catch (error) {
        console.log('Error getting samples: ', error);
        this.assets = [];
        this.isLoading = false;
      }
    },
    // checks which filters are active, then pulls assets when their associated property matches an active filter
    assetTestCriteria: function(asset) {
      if (asset && asset.materialSamplesAvailable) {
        const { materialColor, materialType, materialPattern } = asset;

        const assetProperties = new Set([materialColor.label, materialType.label, materialPattern.label]);
        const applicableFilters = [];

        this.activeFilters.forEach((filter) => {
          if (filter !== '') {
            applicableFilters.push(filter);
          }
        });

        if (applicableFilters.length === 0) {
          return true;
        }

        return applicableFilters.every((label) => assetProperties.has(label));
      }
      return false;
    },
    openModal: function(openOnUrlParams=false) {
      if (openOnUrlParams) {
        EventBus.$emit('open-modal', null, this.urlParamCollectionIds, 'sample-material');
      } else {
        this.$router.push({ path: `?viewCollection=${this.assetIds}` });
        EventBus.$emit('open-modal', null, this.assetIds, 'sample-material');
      }

      //uncheck all selections
      this.selectedSamples.forEach((checked, asset_id) => {
        const gallery_checkbox = this.$refs[`gallery_checkbox_${asset_id}`][0];
        gallery_checkbox.checked = false;
        this.selectedSamples.set(asset_id, false);
      });

    },
    closeLoading: function() {
      this.isLoading = false;
    },
    cancelModal: function() {
      this.closeLoading();
      this.close();
      this.showNotification('There was an issue loading the item');
    },
    showNotification: function(text) {
      this.notificationText = text;
      this.notificationTriggered = true;
      setTimeout(() => this.notificationVisible = true, 250);
    },
    dismissNotification: function() {
      this.notificationVisible = false;
      setTimeout(() => {
        this.notificationText = null;
        this.notificationTriggered = false;
      }, 500);
    }
  },
}
</script>